import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export type ProjectType =
  | "residential_new"
  | "renovation"
  | "commercial_buildout"
  | "site_work"
  | "multi_family"
  | "custom";

export interface Project {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  projectType?: ProjectType;
  targetEndDate?: string | null;
  status: "active" | "archived" | "completed";
  createdAt: string;
  updatedAt?: string;
}

export interface CreateProjectData {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  projectType?: ProjectType;
  targetEndDate?: string;
}

export interface UpdateProjectData {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  projectType?: ProjectType;
  targetEndDate?: string | null;
  status?: "active" | "archived" | "completed";
}

interface ProjectsResponse {
  projects: Project[];
}

interface ProjectResponse {
  project: Project;
}

export function useProjects() {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ProjectsResponse>("/api/projects", {
        headers,
      });
    },
    enabled: auth.isSignedIn,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useProject(id: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", id],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ProjectResponse>(`/api/projects/${id}`, {
        headers,
      });
    },
    enabled: auth.isSignedIn && !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCreateProject() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ProjectResponse>("/api/projects", {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useUpdateProject() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProjectData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<ProjectResponse>(`/api/projects/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", variables.id] });
    },
  });
}

export function useDeleteProject() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(`/api/projects/${id}`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
